import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';

const resolveLegacyVariant = ({ variant, primary, ghost, fancy, text }) => {
  if (variant) return variant;
  if (primary) return 'primary';
  if (ghost) return 'ghost';
  if (text) return 'link';
  if (fancy) return 'outline';
  return 'primary';
};

const Button = ({
  to,
  href,
  children,
  className,
  variant,
  type = 'button',
  leadingIcon,
  icon,
  onClick,
  ariaLabel,
  iconOnly = false,
  openInNewTab = true,
  showExternalIcon = true,
  primary,
  ghost,
  fancy,
  text
}) => {
  const resolvedVariant = resolveLegacyVariant({ variant, primary, ghost, fancy, text });
  const externalHref = href || (to && /^https?:\/\//i.test(to) ? to : undefined);
  const isExternal = Boolean(externalHref);

  const baseClassName = classNames(
    'button',
    `button--${resolvedVariant}`,
    { 'button--icon-only': iconOnly || resolvedVariant === 'icon' },
    className
  );

  const leadingIconName = leadingIcon || icon;
  const canShowLeadingIcon = ['primary', 'outline', 'ghost', 'icon'].includes(resolvedVariant) && leadingIconName;
  const shouldShowExternalIcon = isExternal && openInNewTab && showExternalIcon;

  const content = (
    <>
      {canShowLeadingIcon ? <FontAwesomeIcon className="button__icon button__icon--leading" icon={leadingIconName} /> : null}
      {children ? <span className="button__label">{children}</span> : null}
      {shouldShowExternalIcon ? (
        <FontAwesomeIcon className="button__icon button__icon--trailing" icon="arrow-up-right-from-square" />
      ) : null}
    </>
  );

  if (isExternal) {
    return (
      <a
        className={baseClassName}
        href={externalHref}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        aria-label={ariaLabel}
      >
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <NavLink className={baseClassName} to={to} aria-label={ariaLabel}>
        {content}
      </NavLink>
    );
  }

  return (
    <button className={baseClassName} type={type} onClick={onClick} aria-label={ariaLabel}>
      {content}
    </button>
  );
};

Button.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'outline', 'ghost', 'link', 'icon']),
  type: PropTypes.string,
  leadingIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
  iconOnly: PropTypes.bool,
  openInNewTab: PropTypes.bool,
  showExternalIcon: PropTypes.bool,
  primary: PropTypes.bool,
  ghost: PropTypes.bool,
  fancy: PropTypes.bool,
  text: PropTypes.bool
};

export default Button;
